/**
 * Testimonial Glow — Premium-Zitatkarte mit Glaslook.
 *
 * Social-Proof-Allrounder: ein Kundenzitat auf einer Glaskarte mit großem
 * Anführungszeichen, Avatar (Initiale oder hochgeladenes Bild), Name + Rolle
 * und Sterne-Rating. Funktioniert für echte Stimmen, „Was Händler sagen",
 * Bewertungen oder als ruhiger, hochwertiger Platzfüller.
 *
 * Inputs:
 *   - text     → das Zitat
 *   - subtext  → "Name | Rolle/Ort"   (z. B. "Lena M. | Café-Inhaberin, Köln")
 *   - number   → Sterne 1–5 (Default 5)
 *   - media[0] → optionales Avatar-Bild (sonst Initiale auf Akzent-Disc)
 *
 * Motion:
 *   - Karte skaliert weich rein, Glow-Aura atmet auf.
 *   - Anführungszeichen „fällt" von oben, Zitat blendet zeilenweise auf.
 *   - Sterne poppen einzeln nach (Stagger), Avatar + Name steigen ein.
 *   - Ein Shimmer wandert einmal über das Glas.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { pickMediaSrc } from "../../reel/sceneShared.js";

export const testimonialGlow: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const quote = inputs.text ?? "Seit Allyver finden mich Kund:innen aus der ganzen Region – ohne Provision, ohne Risiko.";
  const [nameRaw, roleRaw] = (inputs.subtext ?? "Lena M. | Café-Inhaberin, Köln").split("|").map((s) => s.trim());
  const name = nameRaw || "";
  const role = roleRaw || "";
  const stars = Math.max(1, Math.min(5, Math.round(parseFloat(inputs.number ?? "5") || 5)));
  const avatarSrc = pickMediaSrc(inputs.media ?? null, 0);
  const initial = (name || "A").slice(0, 1).toUpperCase();

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const cardId = `${clipPrefix}-card`;
  const auraId = `${clipPrefix}-aura`;
  const markId = `${clipPrefix}-mark`;
  const quoteId = `${clipPrefix}-quote`;
  const avatarId = `${clipPrefix}-avatar`;
  const nameId = `${clipPrefix}-name`;
  const shimmerId = `${clipPrefix}-shimmer`;
  const starIds = Array.from({ length: stars }, (_, i) => `${clipPrefix}-star-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const quotePx = isVertical ? md * 0.96 : lg * 0.74;
  const namePx = isVertical ? sm * 0.74 : md * 0.5;
  const rolePx = isVertical ? sm * 0.56 : md * 0.36;
  const starPx = isVertical ? 30 : 36;
  const avatarBox = isVertical ? 76 : 88;
  const cardMaxW = isVertical ? 900 : 1180;

  const starSvg = (id: string) => `<svg id="${id}" width="${starPx}" height="${starPx}" viewBox="0 0 24 24" fill="${accent}" style="filter:drop-shadow(0 0 8px ${withAlpha(accent, 0.7)});"><path d="M12 2l2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.8 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6z"/></svg>`;

  const avatarHtml = avatarSrc
    ? `<img src="${escapeHtml(avatarSrc)}" alt="${escapeHtml(name)}" style="width:${avatarBox}px;height:${avatarBox}px;border-radius:9999px;object-fit:cover;border:2px solid ${withAlpha(accent, 0.7)};box-shadow:0 0 24px ${withAlpha(accent, 0.5)};" />`
    : `<div style="width:${avatarBox}px;height:${avatarBox}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 30% 30%, ${withAlpha(accent, 0.85)} 0%, ${withAlpha(accent, 0.4)} 70%);border:2px solid ${withAlpha(accent, 0.7)};box-shadow:0 0 26px ${withAlpha(accent, 0.55)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${Math.round(avatarBox * 0.42)}px;color:${themeColors.fg};">${escapeHtml(initial)}</div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:${isVertical ? "80px 52px" : "96px 130px"};overflow:hidden;">
    <div id="${auraId}" style="position:absolute;width:${isVertical ? 700 : 900}px;height:${isVertical ? 700 : 900}px;border-radius:9999px;background:radial-gradient(circle, ${withAlpha(accent, 0.32)} 0%, transparent 65%);filter:blur(20px);"></div>
    <div id="${cardId}" style="position:relative;display:flex;flex-direction:column;gap:${isVertical ? 26 : 30}px;width:100%;max-width:${cardMaxW}px;padding:${isVertical ? "44px 40px" : "56px 60px"};border-radius:34px;background:linear-gradient(160deg, ${withAlpha(themeColors.fg, 0.1)} 0%, ${withAlpha(themeColors.bg, 0.72)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.16)};box-shadow:0 30px 80px rgba(0,0,0,0.45), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.12)};overflow:hidden;will-change:transform,opacity;">
      <div id="${markId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${isVertical ? 120 : 150}px;line-height:0.7;color:${accent};opacity:0.85;height:${isVertical ? 60 : 76}px;text-shadow:0 0 40px ${withAlpha(accent, 0.5)};">&ldquo;</div>
      <div id="${quoteId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${quotePx}px;line-height:1.22;letter-spacing:-0.8px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(quote)}</div>
      <div style="display:flex;align-items:center;gap:6px;">${starIds.map((id) => starSvg(id)).join("")}</div>
      <div style="display:flex;align-items:center;gap:${isVertical ? 16 : 20}px;">
        <div id="${avatarId}" style="will-change:transform,opacity;">${avatarHtml}</div>
        <div id="${nameId}" style="display:flex;flex-direction:column;gap:4px;">
          <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${namePx}px;letter-spacing:-0.5px;color:${themeColors.fg};">${escapeHtml(name)}</div>
          ${role ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${rolePx}px;letter-spacing:0.3px;color:${withAlpha(themeColors.fg, 0.66)};">${escapeHtml(role)}</div>` : ""}
        </div>
      </div>
      <div id="${shimmerId}" style="position:absolute;top:-30%;bottom:-30%;width:34%;background:linear-gradient(100deg, transparent 0%, ${withAlpha(themeColors.fg, 0.16)} 50%, transparent 100%);pointer-events:none;mix-blend-mode:screen;transform:rotate(10deg);"></div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${cardId}`, { opacity: 0, y: 36, scale: 0.94, transformOrigin: "50% 60%" }),
    gsapSetLine(`#${auraId}`, { opacity: 0, scale: 0.7 }),
    gsapSetLine(`#${markId}`, { opacity: 0, y: -24 }),
    gsapSetLine(`#${quoteId}`, { opacity: 0, y: 18 }),
    gsapSetLine(`#${avatarId}`, { opacity: 0, x: -16 }),
    gsapSetLine(`#${nameId}`, { opacity: 0, x: -12 }),
    gsapSetLine(`#${shimmerId}`, { opacity: 0, xPercent: -160 }),
    ...starIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0, transformOrigin: "50% 50%" })),
  ].join("\n");

  const tweens: string[] = [
    t.to(`#${auraId}`, { opacity: 1, scale: 1, duration: 0.9, ease: "raw:\"power2.out\"" }, 0),
    t.to(`#${cardId}`, { opacity: 1, y: 0, scale: 1, duration: 0.65, ease: "raw:\"power3.out\"" }, 0.1),
    t.to(`#${markId}`, { opacity: 0.85, y: 0, duration: 0.5, ease: "raw:\"back.out(2)\"" }, 0.3),
    t.to(`#${quoteId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.45),
  ];

  const starBase = 0.95;
  starIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, duration: 0.34, ease: "raw:\"back.out(2.4)\"" }, starBase + i * 0.09));
  });

  const personAt = starBase + stars * 0.09 + 0.1;
  tweens.push(t.to(`#${avatarId}`, { opacity: 1, x: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, personAt));
  tweens.push(t.to(`#${nameId}`, { opacity: 1, x: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, personAt + 0.08));

  // Aura-Atmen für lebendiges Standbild.
  tweens.push(t.to(`#${auraId}`, { scale: 1.08, duration: Math.max(1.2, clipDurationSec - personAt - 0.5), ease: "raw:\"sine.inOut\"", yoyo: true, repeat: 1 }, personAt));

  const shimmerAt = personAt + 0.2;
  const shimmerEnd = clipDurationSec - 10 / 30 - 0.2;
  if (shimmerEnd - shimmerAt > 0.5) {
    tweens.push(t.to(`#${shimmerId}`, { opacity: 1, duration: 0.2, ease: "raw:\"none\"" }, shimmerAt));
    tweens.push(t.to(`#${shimmerId}`, { xPercent: 420, duration: Math.min(1.3, shimmerEnd - shimmerAt - 0.2), ease: "raw:\"power1.inOut\"" }, shimmerAt + 0.05));
    tweens.push(t.to(`#${shimmerId}`, { opacity: 0, duration: 0.25, ease: "raw:\"none\"" }, shimmerEnd - 0.1));
  }

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
